import { useLocation } from 'react-router-dom'

interface SolicitudState {
  nombre_completo?: string
  correo?: string
  telefono?: string
  ciudad?: string
  provincia?: string
  fecha_solicitud?: string
  autorizar_envio_mm?: boolean
  campania_titulo?: string
}

export function YaSolicitadaPage() {
  const location = useLocation()
  const solicitud = (location.state as SolicitudState) || null

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <img
            src="/logo-tonal-negro.png"
            alt="Tonal"
            className="mx-auto h-12 w-auto"
            onError={(e) => { e.currentTarget.style.display = 'none' }}
          />
        </div>

        {/* Icon */}
        <div className="mb-8">
          <div className="mx-auto w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>

        {/* Message */}
        <div className="bg-white shadow-lg rounded-lg p-8 mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
            ¡Ya Has Solicitado!
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Ya has enviado una solicitud{solicitud?.campania_titulo ? <> para <strong>{solicitud.campania_titulo}</strong></> : ' para esta campaña'}.
            Tu solicitud fue recibida exitosamente.
          </p>

          {/* Solicitud Details */}
          {solicitud && (solicitud.nombre_completo || solicitud.correo) && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Detalles de tu solicitud:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {solicitud.nombre_completo && (
                  <div>
                    <span className="font-medium text-gray-700">Nombre:</span>
                    <span className="text-gray-600 ml-2">{solicitud.nombre_completo}</span>
                  </div>
                )}
                {solicitud.correo && (
                  <div>
                    <span className="font-medium text-gray-700">Correo:</span>
                    <span className="text-gray-600 ml-2">{solicitud.correo}</span>
                  </div>
                )}
                {solicitud.telefono && (
                  <div>
                    <span className="font-medium text-gray-700">Teléfono:</span>
                    <span className="text-gray-600 ml-2">{solicitud.telefono}</span>
                  </div>
                )}
                {(solicitud.ciudad || solicitud.provincia) && (
                  <div>
                    <span className="font-medium text-gray-700">Ubicación:</span>
                    <span className="text-gray-600 ml-2">
                      {[solicitud.ciudad, solicitud.provincia].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
                {solicitud.fecha_solicitud && (
                  <div>
                    <span className="font-medium text-gray-700">Fecha:</span>
                    <span className="text-gray-600 ml-2">{solicitud.fecha_solicitud}</span>
                  </div>
                )}
                {solicitud.autorizar_envio_mm !== undefined && (
                  <div>
                    <span className="font-medium text-gray-700">Estado:</span>
                    {solicitud.autorizar_envio_mm ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 ml-2">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Envío Autorizado
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 ml-2">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Sin Autorización
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-left">
                <h3 className="font-medium text-blue-900 mb-2">¿Qué sigue ahora?</h3>
                <div className="text-sm text-blue-800 space-y-2">
                  <p>Nuestro equipo revisará tu solicitud en las próximas 24-48 horas.</p>
                  <p>Te contactaremos por correo electrónico con los detalles del envío.</p>
                  {(solicitud?.autorizar_envio_mm === undefined || solicitud?.autorizar_envio_mm) && (
                    <p>Las muestras médicas serán enviadas a tu dirección registrada.</p>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-blue-200">
                  <p className="text-sm text-blue-800 mb-2">
                    <strong>¿Necesitas ayuda o tienes preguntas?</strong>
                  </p>
                  <div className="text-sm text-blue-800">
                    <p><strong>Email:</strong> a.villota@tonalstrategies.com</p>
                    <p><strong>Teléfono:</strong> +593 98 194 1812</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-500">
            Powered by <strong>Tonal Strategies</strong>
          </p>
        </div>
      </div>
    </div>
  )
}
